import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Sparkles, MessageCircleHeart, TrendingUp, Wind, BookHeart, ArrowRight, Heart, UserCog, X } from "lucide-react";
import FeedbackFooter from "@/components/FeedbackFooter";

const DISMISS_KEY = "cheerup_profile_nudge_dismissed";

const moodEmojis = ["😞", "😔", "😐", "🙂", "😊"];
const moodLabels = ["Rough", "Low", "Okay", "Good", "Great"];

export default function Dashboard() {
    const { user } = useAuth();
    const [affirmation, setAffirmation] = useState(null);
    const [recentMoods, setRecentMoods] = useState([]);
    const [logging, setLogging] = useState(false);
    const [showNudge, setShowNudge] = useState(false);

    useEffect(() => {
        api.get("/affirmations/daily").then(({ data }) => setAffirmation(data));
        api.get("/moods").then(({ data }) => setRecentMoods(data.moods || []));
        // Show profile nudge only if never dismissed AND profile fields empty
        if (localStorage.getItem(DISMISS_KEY) !== "1") {
            api.get("/profile").then(({ data }) => {
                if (!data.gender && !data.dob) setShowNudge(true);
            }).catch(() => { /* silent */ });
        }
    }, []);

    const dismissNudge = () => {
        localStorage.setItem(DISMISS_KEY, "1");
        setShowNudge(false);
    };

    const logMood = async (mood) => {
        setLogging(true);
        try {
            const { data } = await api.post("/moods", { mood, emoji: moodEmojis[mood - 1], note: "" });
            setRecentMoods(prev => [data, ...prev]);
        } finally {
            setLogging(false);
        }
    };

    const hour = new Date().getHours();
    const greeting = hour < 12 ? "Good morning" : hour < 18 ? "Good afternoon" : "Good evening";
    const first = (user?.name || "").split(" ")[0] || "friend";

    return (
        <div className="max-w-6xl mx-auto p-6 sm:p-10 lg:p-14">
            {showNudge && (
                <motion.div
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5 }}
                    className="mb-8 flex items-center gap-3 p-4 rounded-2xl bg-accent border border-border"
                    data-testid="profile-nudge"
                >
                    <UserCog className="w-5 h-5 text-primary flex-shrink-0" strokeWidth={1.5} />
                    <div className="flex-1 min-w-0 text-sm">
                        <span className="font-medium">Complete your profile</span>{" "}
                        <span className="text-muted-foreground">— add your gender and date of birth if you'd like. Totally optional.</span>
                    </div>
                    <Link to="/app/profile" data-testid="profile-nudge-link">
                        <Button size="sm" variant="outline" className="rounded-full bg-background">Add details</Button>
                    </Link>
                    <button onClick={dismissNudge} className="p-1.5 rounded-full hover:bg-white/70 text-muted-foreground hover:text-foreground transition-colors" data-testid="profile-nudge-dismiss" aria-label="Dismiss">
                        <X className="w-4 h-4" strokeWidth={1.5} />
                    </button>
                </motion.div>
            )}

            {/* Header */}
            <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6 }}
                className="mb-10"
            >
                <p className="text-sm uppercase tracking-widest text-muted-foreground mb-3 font-medium">{greeting}</p>
                <h1 className="font-heading text-4xl sm:text-5xl font-[800] tracking-tight">
                    Hey {first}. <span className="font-[300] italic">How are you today?</span>
                </h1>
            </motion.div>

            {/* Quick mood log */}
            <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.7, delay: 0.15 }}
                className="bg-card rounded-3xl p-8 border border-border shadow-soft mb-8"
            >
                <div className="flex items-center gap-2 mb-6">
                    <Heart className="w-4 h-4 text-primary" strokeWidth={1.5} fill="currentColor" />
                    <span className="text-sm font-medium text-muted-foreground">A quick check-in</span>
                </div>
                <div className="grid grid-cols-5 gap-3 sm:gap-4">
                    {moodEmojis.map((e, i) => (
                        <button
                            key={i}
                            disabled={logging}
                            onClick={() => logMood(i + 1)}
                            data-testid={`dashboard-mood-${i + 1}`}
                            className="group flex flex-col items-center gap-2 p-4 sm:p-6 rounded-2xl bg-accent/40 hover:bg-accent hover:-translate-y-1 border border-border transition-transform duration-300 disabled:opacity-50"
                        >
                            <span className="text-4xl sm:text-5xl">{e}</span>
                            <span className="text-xs sm:text-sm font-medium text-muted-foreground group-hover:text-foreground">{moodLabels[i]}</span>
                        </button>
                    ))}
                </div>
            </motion.div>

            {/* Bento grid */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {/* Affirmation - larger */}
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.7, delay: 0.25 }}
                    className="md:col-span-2 relative overflow-hidden bg-gradient-to-br from-primary/15 to-accent rounded-3xl p-8 border border-border shadow-soft"
                >
                    <Sparkles className="w-6 h-6 text-primary mb-6" strokeWidth={1.5} />
                    <p className="text-xs uppercase tracking-widest text-muted-foreground mb-4 font-medium">Today's affirmation</p>
                    <p className="font-heading text-2xl sm:text-3xl font-[300] leading-snug italic mb-6">
                        “{affirmation?.text || "Loading a kind thought…"}”
                    </p>
                    <Link to="/app/affirmations" data-testid="dashboard-affirmations-link">
                        <Button variant="ghost" className="rounded-full font-medium -ml-3">
                            More affirmations <ArrowRight className="w-4 h-4 ml-2" strokeWidth={1.5} />
                        </Button>
                    </Link>
                </motion.div>

                {/* Chat CTA */}
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.7, delay: 0.35 }}
                    className="bg-card rounded-3xl p-8 border border-border shadow-soft flex flex-col"
                >
                    <MessageCircleHeart className="w-6 h-6 text-primary mb-6" strokeWidth={1.5} />
                    <h3 className="font-heading text-xl font-[700] mb-3 tracking-tight">Need a listening ear?</h3>
                    <p className="text-sm text-muted-foreground leading-relaxed mb-6 flex-1">Your companion is here whenever you want to talk.</p>
                    <Link to="/app/chat" data-testid="dashboard-chat-link">
                        <Button className="rounded-full w-full font-medium shadow-soft">
                            Start a chat
                        </Button>
                    </Link>
                </motion.div>

                {/* Breathing */}
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.7, delay: 0.45 }}
                    className="bg-card rounded-3xl p-8 border border-border shadow-soft"
                >
                    <Wind className="w-6 h-6 text-primary mb-6" strokeWidth={1.5} />
                    <h3 className="font-heading text-xl font-[700] mb-3 tracking-tight">Feel your breath</h3>
                    <p className="text-sm text-muted-foreground leading-relaxed mb-6">A 2-minute guided breathing exercise.</p>
                    <Link to="/app/breathing" data-testid="dashboard-breathing-link">
                        <Button variant="outline" className="rounded-full w-full font-medium bg-background">Breathe</Button>
                    </Link>
                </motion.div>

                {/* Mood insights */}
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.7, delay: 0.5 }}
                    className="bg-card rounded-3xl p-8 border border-border shadow-soft md:col-span-2"
                    data-testid="dashboard-recent-moods"
                >
                    <div className="flex items-center justify-between mb-6">
                        <div className="flex items-center gap-2">
                            <TrendingUp className="w-5 h-5 text-primary" strokeWidth={1.5} />
                            <h3 className="font-heading text-lg font-[700] tracking-tight">Recent check-ins</h3>
                        </div>
                        <Link to="/app/moods" className="text-sm font-medium text-primary hover:underline">See all</Link>
                    </div>
                    {recentMoods.length === 0 ? (
                        <div className="text-sm text-muted-foreground py-6">Your check-ins will show up here. Tap an emoji above whenever.</div>
                    ) : (
                        <div className="flex gap-2 sm:gap-3 flex-wrap">
                            {recentMoods.slice(0, 10).map(m => (
                                <div key={m.id} className="flex items-center gap-2 px-4 py-2 rounded-full bg-accent/50 border border-border">
                                    <span className="text-xl">{m.emoji || moodEmojis[m.mood - 1]}</span>
                                    <span className="text-xs text-muted-foreground">{new Date(m.created_at).toLocaleDateString(undefined, { month: "short", day: "numeric" })}</span>
                                </div>
                            ))}
                        </div>
                    )}
                </motion.div>
            </div>
            <FeedbackFooter />
        </div>
    );
}
